import { DashIcon, PlusIcon, ScreenFullIcon } from "@primer/octicons-react";
import { useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

export interface GNode {
  id: string;
  label: string;
  /** A CSS color, such as `var(--accent)`. */
  color: string;
  /** Radius in px before zoom. */
  r?: number;
  title?: string;
}

export interface GEdge {
  id: string;
  from: string;
  to: string;
  label?: string;
  weight?: number;
}

const W = 900;
const H = 560;

interface Placed extends GNode {
  x: number;
  y: number;
  r: number;
}

/** A small force-directed layout (repulsion, springs, gravity); deterministic, so a graph does not jump between renders. */
function layout(nodes: GNode[], edges: GEdge[]): Placed[] {
  const n = nodes.length;
  const p: Placed[] = nodes.map((g, i) => {
    const a = (i / Math.max(n, 1)) * Math.PI * 2;
    const ring = 60 + 14 * Math.sqrt(n) * ((i % 3) + 1);
    return { ...g, r: g.r ?? 7, x: Math.cos(a) * ring, y: Math.sin(a) * ring };
  });
  const at = new Map(p.map((q, i) => [q.id, i]));
  const links = edges.flatMap((e) => {
    const a = at.get(e.from);
    const b = at.get(e.to);
    return a === undefined || b === undefined || a === b ? [] : [{ a, b, w: Math.min(1 + Math.log1p(e.weight ?? 1) * 0.15, 2) }];
  });
  const k = 70;
  const iters = n > 150 ? 120 : 260;
  for (let it = 0; it < iters; it++) {
    const t = 1 - it / iters;
    const fx = new Float64Array(n);
    const fy = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        let dx = p[i].x - p[j].x;
        let dy = p[i].y - p[j].y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 0.01) {
          dx = (i % 2) - 0.5;
          dy = (j % 2) - 0.5;
          d2 = 0.5;
        }
        if (d2 > 90000) continue;
        const f = (k * k) / d2;
        fx[i] += dx * f;
        fy[i] += dy * f;
        fx[j] -= dx * f;
        fy[j] -= dy * f;
      }
    }
    for (const { a, b, w } of links) {
      const dx = p[b].x - p[a].x;
      const dy = p[b].y - p[a].y;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const f = ((d - k) / d) * 0.12 * w;
      fx[a] += dx * f;
      fy[a] += dy * f;
      fx[b] -= dx * f;
      fy[b] -= dy * f;
    }
    for (let i = 0; i < n; i++) {
      fx[i] -= p[i].x * 0.02;
      fy[i] -= p[i].y * 0.02;
      const m = Math.hypot(fx[i], fy[i]) || 1;
      const step = Math.min(m, 24 * t + 1);
      p[i].x += (fx[i] / m) * step;
      p[i].y += (fy[i] / m) * step;
    }
  }
  return p;
}

/** An SVG graph with pan (drag), zoom (buttons, or the wheel with Ctrl/⌘), hover highlighting and click to select. */
export function GraphCanvas({
  nodes,
  edges,
  selected,
  onSelect,
  directed = false,
  className,
}: {
  nodes: GNode[];
  edges: GEdge[];
  selected?: string | null;
  onSelect?: (id: string | null) => void;
  directed?: boolean;
  className?: string;
}) {
  const placed = useMemo(() => layout(nodes, edges), [nodes, edges]);
  const byId = useMemo(() => new Map(placed.map((p) => [p.id, p])), [placed]);
  const bounds = useMemo(() => {
    if (!placed.length) return { x: -W / 2, y: -H / 2, w: W, h: H };
    const xs = placed.map((p) => p.x);
    const ys = placed.map((p) => p.y);
    const pad = 70;
    const x0 = Math.min(...xs) - pad;
    const y0 = Math.min(...ys) - pad;
    const w = Math.max(Math.max(...xs) + pad - x0, 240);
    const h = Math.max(Math.max(...ys) + pad - y0, 160);
    const s = Math.max(w / W, h / H);
    return { x: x0 - (W * s - w) / 2, y: y0 - (H * s - h) / 2, w: W * s, h: H * s };
  }, [placed]);
  // The view belongs to one layout: a new graph starts at the default view.
  const [view, setView] = useState<{ key: unknown; dx: number; dy: number; z: number }>({ key: bounds, dx: 0, dy: 0, z: 1 });
  const v = view.key === bounds ? view : { key: bounds, dx: 0, dy: 0, z: 1 };
  const [hover, setHover] = useState<string | null>(null);
  const drag = useRef<{ x: number; y: number; moved: boolean } | null>(null);
  const svg = useRef<SVGSVGElement>(null);

  const focusId = hover ?? selected ?? null;
  const near = useMemo(() => {
    if (!focusId) return null;
    const s = new Set([focusId]);
    for (const e of edges) {
      if (e.from === focusId) s.add(e.to);
      if (e.to === focusId) s.add(e.from);
    }
    return s;
  }, [focusId, edges]);

  const vw = bounds.w / v.z;
  const vh = bounds.h / v.z;
  const vx = bounds.x + (bounds.w - vw) / 2 + v.dx;
  const vy = bounds.y + (bounds.h - vh) / 2 + v.dy;
  const zoom = (f: number) => setView({ ...v, z: Math.min(Math.max(v.z * f, 0.3), 6) });
  // Few nodes: label them all. Many: only the highlighted neighbourhood, to stay legible.
  const showAll = placed.length <= 40;

  return (
    <div className={cn("relative overflow-hidden rounded-md border bg-inset", className)}>
      <svg
        ref={svg}
        viewBox={`${vx} ${vy} ${vw} ${vh}`}
        className="block h-full w-full touch-none select-none"
        role="img"
        aria-label="Graph"
        onPointerDown={(e) => {
          drag.current = { x: e.clientX, y: e.clientY, moved: false };
          svg.current?.setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d || !svg.current) return;
          const r = svg.current.getBoundingClientRect();
          if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) d.moved = true;
          if (!d.moved) return;
          setView({ ...v, dx: v.dx - (e.clientX - d.x) * (vw / r.width), dy: v.dy - (e.clientY - d.y) * (vh / r.height) });
          d.x = e.clientX;
          d.y = e.clientY;
        }}
        onPointerUp={() => {
          if (drag.current && !drag.current.moved) onSelect?.(null);
          drag.current = null;
        }}
        onWheel={(e) => (e.ctrlKey || e.metaKey) && zoom(e.deltaY < 0 ? 1.15 : 1 / 1.15)}
      >
        {directed && (
          <defs>
            <marker id="graph-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
              <path d="M0 0L10 5L0 10z" fill="var(--fg-subtle)" />
            </marker>
          </defs>
        )}
        {edges.map((e) => {
          const a = byId.get(e.from);
          const b = byId.get(e.to);
          if (!a || !b) return null;
          const lit = focusId != null && (e.from === focusId || e.to === focusId);
          const dim = focusId != null && !lit;
          const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
          const ux = (b.x - a.x) / len;
          const uy = (b.y - a.y) / len;
          return (
            <g key={e.id} opacity={dim ? 0.12 : 1}>
              <line
                x1={a.x + ux * a.r}
                y1={a.y + uy * a.r}
                x2={b.x - ux * (b.r + (directed ? 3 : 0))}
                y2={b.y - uy * (b.r + (directed ? 3 : 0))}
                stroke={lit ? "var(--accent)" : "var(--fg-subtle)"}
                strokeWidth={lit ? 1.8 : 1}
                strokeOpacity={lit ? 1 : 0.55}
                markerEnd={directed ? "url(#graph-arrow)" : undefined}
              />
              {lit && e.label && (
                <text x={(a.x + b.x) / 2} y={(a.y + b.y) / 2 - 4} textAnchor="middle" fontSize={10} fill="var(--fg-muted)" stroke="var(--bg-inset)" strokeWidth={3} paintOrder="stroke">
                  {e.label}
                </text>
              )}
            </g>
          );
        })}
        {placed.map((p) => {
          const isSel = p.id === selected;
          const dim = near != null && !near.has(p.id);
          const label = showAll || (near?.has(p.id) ?? false);
          return (
            <g
              key={p.id}
              transform={`translate(${p.x} ${p.y})`}
              opacity={dim ? 0.2 : 1}
              className="cursor-pointer"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onSelect?.(isSel ? null : p.id);
              }}
              onPointerEnter={() => setHover(p.id)}
              onPointerLeave={() => setHover(null)}
            >
              <title>{p.title ?? p.label}</title>
              <circle r={p.r + (isSel ? 3 : 0)} fill={p.color} stroke={isSel ? "var(--fg)" : "var(--bg)"} strokeWidth={isSel ? 2 : 1.5} />
              {label && (
                <text y={p.r + 12} textAnchor="middle" fontSize={11} fill="var(--fg)" stroke="var(--bg-inset)" strokeWidth={3} paintOrder="stroke" pointerEvents="none">
                  {p.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="absolute right-2 top-2 flex flex-col gap-1">
        <Button size="icon" aria-label="Zoom in" onClick={() => zoom(1.25)}>
          <PlusIcon />
        </Button>
        <Button size="icon" aria-label="Zoom out" onClick={() => zoom(0.8)}>
          <DashIcon />
        </Button>
        <Button size="icon" aria-label="Reset view" onClick={() => setView({ key: bounds, dx: 0, dy: 0, z: 1 })}>
          <ScreenFullIcon />
        </Button>
      </div>
    </div>
  );
}
